import type { BookableSlot } from '../../types';
import { addDays, parseLocalDate, toISODate } from '../../utils/date-helpers';

export function getSlotDate(slot: BookableSlot): string | undefined {
  return slot.slot_date ?? slot.slot_start?.slice(0, 10);
}

export function getSlotHour(slot: BookableSlot): number {
  const timeLabel = slot.slot_start_time_string ?? slot.slot_start_string;
  const parsedHour = Number(timeLabel?.slice(0, 2));
  if (Number.isFinite(parsedHour)) return parsedHour;
  return new Date(slot.slot_start).getHours();
}

export function getSlotTimeLabel(slot: BookableSlot): string {
  return slot.slot_start_time_string ?? slot.slot_start_string ?? slot.slot_start;
}

export function getFirstAvailableSlotDate(slots: BookableSlot[], weekStart: string): string | undefined {
  const dates = new Set(
    slots.map((slot): string | undefined => getSlotDate(slot)).filter((date): date is string => Boolean(date)),
  );
  const today = toISODate(new Date());
  const weekStartDate = parseLocalDate(weekStart);

  for (let index = 0; index < 7; index += 1) {
    const date = toISODate(addDays(weekStartDate, index));
    if (date >= today && dates.has(date)) return date;
  }

  return Array.from(dates).sort()[0];
}

/**
 * Jour à sélectionner dans la grille, en privilégiant celui demandé par un lien profond (`?d=`).
 *
 * Le jour demandé n'est retenu que s'il est dans la semaine affichée, non passé, ET qu'il porte
 * réellement des créneaux — sinon on retombe sur le premier jour disponible. Sélectionner un jour
 * vide « au nom du lien » afficherait une colonne sans créneau et donnerait l'impression que le
 * professionnel est complet.
 */
export function getPreferredSlotDate(
  slots: BookableSlot[],
  weekStart: string,
  preferredDate: string | undefined,
): string | undefined {
  if (preferredDate) {
    const dates = new Set(
      slots.map((slot): string | undefined => getSlotDate(slot)).filter((date): date is string => Boolean(date)),
    );
    const weekStartDate = parseLocalDate(weekStart);
    const weekEnd = toISODate(addDays(weekStartDate, 6));
    const today = toISODate(new Date());

    if (preferredDate >= weekStart && preferredDate <= weekEnd && preferredDate >= today && dates.has(preferredDate)) {
      return preferredDate;
    }
  }

  return getFirstAvailableSlotDate(slots, weekStart);
}
